import { Experiment as MarvelExperiment, Variant as MarvelVariant } from '@marvelapp/react-ab-test';
import marvelEmitter from '@marvelapp/react-ab-test/lib/emitter';
import { useGetActivePlans } from 'imports/checkout/hooks/useGetActivePlans';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import styled, { css } from 'styled-components';
import { Push } from '/components/Link';
import {
  CLOSE_PRICING_COUNTRY,
  CONTROL_BROSWER_RUN_MOBILE,
  FULLY_NON_TRAIL_COUNTRY,
  FULLY_QUARTERLY_COUNTRY,
  NEW_PRICE_FOR_BROWSER_AND_RUN_MOBILE,
  NEW_PRICING_EXP_3_BILLING_VERSIONS,
} from '/imports/checkout/api/constants';
import { billingCycleWiseText, getAbtestWisePlansAmount, isWithNonTrailEnabled } from '/imports/checkout/api/helpers';
import useBilling from '/imports/checkout/api/useBilling';
import { getCountry, helperCurrencyFormatter } from '/imports/checkout/api/utils';
import { ACCPTED_CARDS_LOGO, BLUEPILL_HOSTS, ONE_YEAR_HIDE_HOST } from '/imports/core/api/constants';
import { useResponsive } from '/imports/core/api/responsiveContext';
import useIntl from '/imports/core/api/useIntl';
import useWindowSize from '/imports/core/api/useWindowSize';
import useDomain from '/imports/core/hooks/useDomain';
import Flex from '/imports/core/ui/atoms/Flex';
import Address from '/imports/core/ui/components/Address';
import { replaceString } from '/imports/core/ui/helpers';
import { footerLinks } from '/imports/homepage/api/constants';
import Container from '/imports/homepageV2/ui/atmos/Container';
import {
  FIXED_DESIGN_MOBILE_WON_LOCALE,
  getCompanyNameByHost,
  getCompanyNumber,
  getFAQRedirectionUrl,
  HIDEONEYEAR_DOMAINS,
  NEW_CHECKOUT_EXP_MOBILE_WON_HOST,
  NEW_DESIGN_MOBILE_WON_LOCALE,
  NEW_SYMBOL_PLACEMENT_LOCALE,
  NEWLY_CHECKOUT_EXP_DESKTOP_WON_LOCALE,
  NEWLY_CHECKOUT_WON_HOST_DESKTOP,
  POSTFIX_PLACEMENT_SYMBOL_CURRENCY,
  staticFile,
} from '/lib/helpers';

const Footer = ({ isNewMobileDesign = false }) => {
  let finalAmount = '';
  const { width } = useWindowSize();
  const router = useRouter();
  const isR1 = router.pathname === '/r1';
  const isR2 = router.pathname === '/r2';
  const { currency } = useBilling();
  const { t, locale } = useIntl();
  const { host } = useResponsive();
  const responsive = useResponsive();
  const { getContactEmail } = useDomain();

  const companyRegistrationNumber = useMemo(() => {
    if (host !== 'cvtoolspro') return false;
    return getCompanyNumber(host);
  }, [host]);
  const links = footerLinks(host, locale);
  const turkeyPlans = marvelEmitter.getActiveVariant('exp_turkey_plans');
  const southAfricaPlans = marvelEmitter.getActiveVariant('exp_southafrica_plans');
  const philippinesPlans = marvelEmitter.getActiveVariant('exp_philippines_plans');
  const brazilPlans = marvelEmitter.getActiveVariant('exp_brazil_plans');
  const country = getCountry();
  const isMobile = width < 980;
  const show3PlansOnDesk =
    (!NEWLY_CHECKOUT_EXP_DESKTOP_WON_LOCALE.includes(locale) &&
      locale !== 'ka' &&
      !NEWLY_CHECKOUT_WON_HOST_DESKTOP.includes(host) &&
      !isMobile) ||
    turkeyPlans == '10m6w16q' ||
    southAfricaPlans == '9m4w14q_day_wise' ||
    philippinesPlans == '9m5w16q' ||
    philippinesPlans == '9m5w16q_day_wise' ||
    brazilPlans == '9m4w15q' ||
    isR2;

  const show3PlansOnMobile =
    (isMobile &&
      !NEW_DESIGN_MOBILE_WON_LOCALE.includes(locale) &&
      !NEW_CHECKOUT_EXP_MOBILE_WON_HOST.includes(host) &&
      !FIXED_DESIGN_MOBILE_WON_LOCALE.includes(locale)) ||
    turkeyPlans == '10m6w16q' ||
    southAfricaPlans == '9m4w14q_day_wise' ||
    philippinesPlans == '9m5w16q' ||
    philippinesPlans == '9m5w16q_day_wise' ||
    brazilPlans == '9m4w15q' ||
    isR2;
  const WithNonTrial = isWithNonTrailEnabled(country, isMobile, host) || isR1 || isR2;
  const oneYearHideHost = ONE_YEAR_HIDE_HOST.includes(host) && !WithNonTrial;

  const [showCookiesSetting, setShowCookiesSetting] = useState(false);
  const handleRedirection = (path) => () => Push(path, locale);
  const isAutoApply = BLUEPILL_HOSTS.includes(responsive?.host);
  const threePlansOffer = marvelEmitter.getActiveVariant('exp_three_offer_non_trial_plans');
  const withNontrail3 = threePlansOffer === 'with_non_trial_3';

  const shouldShowMidPlan =
    ((!WithNonTrial || threePlansOffer === 'control') && (show3PlansOnDesk || show3PlansOnMobile)) ||
    turkeyPlans == '10m6w16q' ||
    southAfricaPlans == '9m4w14q_day_wise' ||
    philippinesPlans == '9m5w16q' ||
    philippinesPlans == '9m5w16q_day_wise' ||
    brazilPlans == '9m4w15q' ||
    isR2;
  const mexicoPlans = marvelEmitter.getActiveVariant('exp_mexico_plans');
  const australiaPlans = marvelEmitter.getActiveVariant('exp_australia_plans');
  const ukarineGreccePlans = marvelEmitter.getActiveVariant('exp_ukraine_greece_plans');
  const indonesiaPlans = marvelEmitter.getActiveVariant('exp_indonesia_plans');
  const ParaguayPlans = marvelEmitter.getActiveVariant('exp_paraguay_plans');
  const pakistanPlans = marvelEmitter.getActiveVariant('exp_pakistan_plans');
  const argentinaPlans = marvelEmitter.getActiveVariant('exp_argentina_plans');
  const lebanondPlans = marvelEmitter.getActiveVariant('exp_lebanon_plans');
  const israelPlans = marvelEmitter.getActiveVariant('exp_israel_plans');
  const fullyQuerterlyCountry = FULLY_QUARTERLY_COUNTRY.includes(country);

  const { activePlansData } = useGetActivePlans(oneYearHideHost, isAutoApply, isR2, isR1, false, true);

  const getPlansFinalAmount = (plans) => {
    return (finalAmount = !plans?.trialAmount
      ? getAbtestWisePlansAmount(host, isMobile, plans).amount
      : getAbtestWisePlansAmount(host, isMobile, plans).trialAmount);
  };

  const finalPrice0 = getPlansFinalAmount(activePlansData[0]);
  const finalPrice1 = getPlansFinalAmount(activePlansData[1]);

  const cheaperPlanInfo =
    finalPrice0 <= finalPrice1
      ? { index: 0, price: finalPrice0, plan: activePlansData[0] }
      : { index: 1, price: finalPrice1, plan: activePlansData[1] };

  if (cheaperPlanInfo.index === 1) {
    activePlansData.reverse();
  }

  const isQuartlyPlans =
    fullyQuerterlyCountry ||
    withNontrail3 ||
    southAfricaPlans == 'var_1' ||
    mexicoPlans == 'var_2' ||
    ukarineGreccePlans == 'var_1' ||
    ukarineGreccePlans == 'var_1_day_wise' ||
    indonesiaPlans == 'var_2' ||
    ParaguayPlans == 'var_2' ||
    turkeyPlans == 'var_2' ||
    turkeyPlans == '10m6w16q' ||
    pakistanPlans == 'var_2' ||
    israelPlans == 'var_2' ||
    philippinesPlans == '16q5w' ||
    threePlansOffer === 'with_non_trial_3_day_wise' ||
    southAfricaPlans === 'var_1_day_wise' ||
    southAfricaPlans == '9m4w14q_day_wise' ||
    brazilPlans == '9m4w15q' ||
    philippinesPlans == '9m5w16q' ||
    philippinesPlans == '9m5w16q_day_wise' ||
    isR1 ||
    argentinaPlans == '15q8m' ||
    lebanondPlans == '14q8m';

  const isThreePlans =
    turkeyPlans == '10m6w16q' ||
    philippinesPlans == '9m5w16q' ||
    philippinesPlans == '9m5w16q_day_wise' ||
    southAfricaPlans == '9m4w14q_day_wise' ||
    brazilPlans == '9m4w15q';

  if (turkeyPlans === 'var_3') {
    activePlansData.reverse();
  }

  useEffect(() => {
    setTimeout(() => {
      if (window?.axeptioSDK) {
        setShowCookiesSetting(true);
      }
      if (window.Cookiebot) {
        setShowCookiesSetting(true);
      }
    }, 3000);
  }, [locale]);

  const openCookieSettings = () => {
    if (window?.axeptioSDK) {
      handleRedirection('javascript:openAxeptioCookies()');
    } else if (window?.Cookiebot) {
      window.Cookiebot.renew();
    }
  };

  const priceDisplay = (slugKey, price, finalPrice, currency, locale, replaceValue, replaceValue2, selectedPlan) => {
    const priceParts = helperCurrencyFormatter(price, currency, locale);
    const currencySymbol =
      priceParts.find((part) => ['currency', 'currency-prefix', 'currency-postfix'].includes(part.type))?.value || '';

    const shouldPlaceSymbolAfter =
      NEW_SYMBOL_PLACEMENT_LOCALE.includes(locale) || POSTFIX_PLACEMENT_SYMBOL_CURRENCY.includes(currency);

    const formatPrice = (value) => {
      const effectiveLocale = country == 'VN' ? 'vi-VN' : 'en-US';

      const formatted = new Intl.NumberFormat(effectiveLocale, {
        style: 'decimal',
        minimumFractionDigits: country == 'VN' ? 0 : 2,
        maximumFractionDigits: country == 'VN' ? 0 : 2,
      }).format(value);

      return shouldPlaceSymbolAfter ? `${formatted}${currencySymbol}` : `${currencySymbol}${formatted}`;
    };

    let translationString = t(slugKey)?.replace(/\$/g, '') || '';

    if (isQuartlyPlans && turkeyPlans !== '10m6w16q') {
      if (selectedPlan?.name == 'Weekly Plan') {
        translationString = translationString.replaceAll(7, 7);
        translationString = translationString.replace(t('payment_week'), t('payment_week'));
      } else if (selectedPlan?.name == 'Quarterly Plan') {
        translationString = translationString.replaceAll(7, 90);
        translationString = translationString.replace(t('payment_week'), t('payment_quarter'));
      }
    } else if (isThreePlans || philippinesPlans == '16q5w') {
      const data = billingCycleWiseText(selectedPlan?.billingCycle);
      if (translationString.includes(30))
        translationString = translationString.replaceAll(30, selectedPlan?.billingCycle);
      else translationString = translationString.replaceAll(7, selectedPlan?.billingCycle);
      translationString = translationString.replaceAll(
        philippinesPlans == '16q5w' ? t('payment_month') : t('payment_week'),
        t(data.text),
      );
    }

    if (australiaPlans == 'var_2' || isR2) {
      if (selectedPlan?.name === 'Yearly Plan') {
        translationString = translationString.replaceAll(30, 365);
        translationString = translationString.replace(t('payment_month'), t('year'));
      }
      if (selectedPlan?.name === 'Quarterly Plan') {
        translationString = translationString.replaceAll(7, 90);
        translationString = translationString.replace(t('payment_week'), t('payment_quarter'));
      }
      if (selectedPlan?.name === 'Monthly Plan') {
        translationString = translationString.replaceAll(7, 30);
        translationString = translationString.replace(t('payment_week'), t('payment_month'));
      }
    }

    const formattedOriginal = formatPrice(price);
    const formattedFinal = formatPrice(finalPrice);

    const generateVariants = (value, replaceWith) => {
      const strVal = value?.toString();
      return {
        [strVal]: replaceWith,
        [strVal?.replace('.', ',')]: replaceWith,
        [strVal?.replace(',', '.')]: replaceWith,
      };
    };

    let replacements = {};

    if (WithNonTrial && threePlansOffer !== 'control') {
      replacements = {
        ...generateVariants(replaceValue, formattedFinal),
      };
    } else {
      replacements = {
        ...generateVariants(replaceValue, formattedOriginal),
        ...generateVariants(replaceValue2, formattedFinal),
      };
    }

    return replaceString(translationString, replacements);
  };

  const getFooterTitle1 = () => {
    if (turkeyPlans == 'var_3' || isThreePlans || isR2) {
      return t('resume_nation_landing_footer_monthly_title');
    }

    if (isQuartlyPlans) {
      return t('resume_nation_landing_footer_quarterly_title');
    }

    if (!WithNonTrial || threePlansOffer === 'control') {
      return t('resume_nation_landing_footer_p7');
    }

    return australiaPlans == 'var_2'
      ? t('resume_nation_landing_footer_monthly_title')
      : t('resume_nation_landing_footer_weekly_title');
  };
  const isMonthFirstWeekLater =
    threePlansOffer == '20m10w' || mexicoPlans == '10m6w' || threePlansOffer == '20m10w_day_wise';
  const renderLinks = (filterCondition) =>
    links.filter(filterCondition).map((item, index) => {
      let modifiedHref = item?.href;

      if (item?.href === '/checkout') {
        const queryParams = new URLSearchParams();
        if (isR1) queryParams.set('isR1', 'true');
        if (isR2) queryParams.set('isR2', 'true');

        if ([...queryParams].length > 0) {
          modifiedHref += `?${queryParams.toString()}`;
        }
      }

      return (
        <Flex key={index} $direction="row">
          <LinkWrapper key={index} $direction="row" onClick={handleRedirection(modifiedHref)}>
            {item.text ? item.text : t(item.slug)}
          </LinkWrapper>
        </Flex>
      );
    });

  if (!activePlansData) return null;
  return (
    <Main $direction="column" $alignItens="center" $justifyContent="center" role="contentinfo">
      <Wrapper $justifyContent="center" $alignItems="center">
        <StyledContainer $isNewMobileDesign={isNewMobileDesign}>
          <Title $isNewMobileDesign={isNewMobileDesign}>{t('resume_nation_landing_footer_p1')}</Title>
          <StyledFlex
            $hideOneYear={HIDEONEYEAR_DOMAINS.includes(host)}
            $isNewMobileDesign={isNewMobileDesign}
            $isMonthFirstWeekLater={isMonthFirstWeekLater}
          >
            <Options
              $isHalf={!show3PlansOnDesk && !show3PlansOnMobile}
              $isOneShow={!show3PlansOnDesk && !show3PlansOnMobile && oneYearHideHost}
            >
              <Plans $direction="column">
                <PlanTitle>{getFooterTitle1()}</PlanTitle>
                <PlanDescription $isNewMobileDesign={isNewMobileDesign}>
                  {activePlansData &&
                    priceDisplay(
                      turkeyPlans == 'var_3' || isThreePlans || isR2
                        ? 'resume_nation_landing_footer_nontrail_monthly'
                        : WithNonTrial && threePlansOffer !== 'control'
                          ? 'resume_nation_landing_footer_nontrail_weekly'
                          : 'resume_nation_landing_footer_p2',
                      activePlansData[0]?.trialAmount,
                      activePlansData[0]?.amount,
                      currency,
                      locale,
                      WithNonTrial && threePlansOffer !== 'control' ? 'AMOUNT' : '1.45',
                      '29.99',
                      activePlansData[0],
                    )}
                </PlanDescription>
              </Plans>
            </Options>
            {shouldShowMidPlan && (
              <Options>
                <Plans $direction="column">
                  <PlanTitle>
                    {t(
                      isR2
                        ? 'resume_nation_landing_footer_quarterly_title'
                        : isThreePlans
                          ? 'resume_nation_landing_footer_weekly_title'
                          : 'resume_nation_landing_footer_p3',
                    )}
                  </PlanTitle>
                  <PlanDescription $isNewMobileDesign={isNewMobileDesign}>
                    {activePlansData &&
                      priceDisplay(
                        isThreePlans || isR2
                          ? 'resume_nation_landing_footer_nontrail_weekly'
                          : 'resume_nation_landing_footer_p4',
                        activePlansData[1]?.trialAmount,
                        activePlansData[1]?.amount,
                        currency,
                        locale,
                        WithNonTrial ? 'AMOUNT' : '1.95',
                        '29.99',
                        activePlansData[1],
                      )}
                  </PlanDescription>
                </Plans>
              </Options>
            )}
            {!oneYearHideHost && (
              <Options $isHalf={!show3PlansOnDesk && !show3PlansOnMobile}>
                <Plans $direction="column">
                  <PlanTitle>
                    {isThreePlans || philippinesPlans == '16q5w'
                      ? t('resume_nation_landing_footer_quarterly_title')
                      : turkeyPlans == 'var_3'
                        ? t('resume_nation_landing_footer_weekly_title')
                        : WithNonTrial && threePlansOffer !== 'control'
                          ? australiaPlans == 'var_2' || isR2
                            ? t('resume_nation_landing_footer_yearly_title')
                            : t('resume_nation_landing_footer_monthly_title')
                          : t('resume_nation_landing_footer_p5')}
                  </PlanTitle>
                  <PlanDescription $isNewMobileDesign={isNewMobileDesign}>
                    {activePlansData &&
                      priceDisplay(
                        turkeyPlans == 'var_3' || isThreePlans
                          ? 'resume_nation_landing_footer_nontrail_weekly'
                          : WithNonTrial && threePlansOffer !== 'control'
                            ? 'resume_nation_landing_footer_nontrail_monthly'
                            : 'resume_nation_landing_footer_p6',
                        activePlansData[show3PlansOnDesk || show3PlansOnMobile ? 2 : 1]?.trialAmount,
                        WithNonTrial && threePlansOffer !== 'control'
                          ? activePlansData[show3PlansOnDesk || show3PlansOnMobile ? 2 : 1]?.amount
                          : activePlansData[show3PlansOnDesk || show3PlansOnMobile ? 2 : 1]?.amount,
                        currency,
                        locale,
                        WithNonTrial && threePlansOffer !== 'control' ? 'AMOUNT' : '9.99',
                        '89.99',
                        activePlansData[show3PlansOnDesk || show3PlansOnMobile ? 2 : 1],
                      )}
                  </PlanDescription>
                </Plans>
              </Options>
            )}
          </StyledFlex>
          <AccpetedPayment $alignItems="center" $isNewMobileDesign={isNewMobileDesign}>
            <Text $isNewMobileDesign={isNewMobileDesign}>{t('footer_payment_options')}</Text>
            <CardWrapper $isNewMobileDesign={isNewMobileDesign}>
              {ACCPTED_CARDS_LOGO.map((k, i) => (
                <PaymentLogo role="img" alt={k.alt} $image={k.img} key={i} />
              ))}
            </CardWrapper>
          </AccpetedPayment>
        </StyledContainer>
      </Wrapper>
      <FottorWrapper $justifyContent="center" $alignItems="center" $direction="column">
        <LinkFlex $isNewMobileDesign={isNewMobileDesign}>
          <Flex $direction="row">
            <LinkWrapper $direction="row" onClick={handleRedirection('/login')}>
              {t('landing.login')}
            </LinkWrapper>
          </Flex>
          {renderLinks((item) => item.isFirstLine)}
        </LinkFlex>
        <LinkFlex $isNewMobileDesign={isNewMobileDesign}>
          {renderLinks((item) => !item.isFirstLine)}
          {showCookiesSetting && (
            <Flex $direction="row">
              <LinkWrapper $direction="row" onClick={openCookieSettings}>
                {t('footer.cookies_settings')}
              </LinkWrapper>
            </Flex>
          )}
          <LinkWrapper $direction="row" onClick={handleRedirection('/legal-notice')}>
            {t('footer.legal_notice')}
          </LinkWrapper>
          <LinkWrapper $direction="row" onClick={() => window.open(getFAQRedirectionUrl(host, locale), '_blank')}>
            {t('faq')}
          </LinkWrapper>
        </LinkFlex>

        <CompanyName $newDesign>{getCompanyNameByHost(host)}</CompanyName>
        <Address $newDesign $isNewMobileDesign={isNewMobileDesign} />
        {host === 'cvtoolspro' && (
          <CompanySupportMail>
            {t('email')}: {getContactEmail()}
          </CompanySupportMail>
        )}
        {companyRegistrationNumber && (
          <CompanyRegistrationNumber>
            {t('footer.company_registration_number')} {companyRegistrationNumber}
          </CompanyRegistrationNumber>
        )}
      </FottorWrapper>
    </Main>
  );
};

const ExpPricing = (props) => {
  const router = useRouter();
  const isR1 = router.pathname === '/r1';
  const isR2 = router.pathname === '/r2';
  const isRroute = isR1 || isR2;
  const country = getCountry();
  const { billingVersion, currency } = useBilling();
  const { width } = useWindowSize();
  if (!NEW_PRICING_EXP_3_BILLING_VERSIONS.includes(billingVersion) || isRroute) {
    return <Footer {...props} />;
  }
  if (CLOSE_PRICING_COUNTRY.includes(country) || currency === 'COP' || FULLY_NON_TRAIL_COUNTRY.includes(country))
    return <Footer {...props} />;
  if (
    width >= 450 &&
    (NEW_PRICE_FOR_BROWSER_AND_RUN_MOBILE.includes(country) || CONTROL_BROSWER_RUN_MOBILE.includes(country))
  )
    return <Footer {...props} />;
  return (
    <MarvelExperiment name="exp_initial_pricing">
      <MarvelVariant name="control">
        <Footer {...props} />
      </MarvelVariant>
      <MarvelVariant name="new_pricing">
        <Footer {...props} />
      </MarvelVariant>
    </MarvelExperiment>
  );
};

export default ExpPricing;

const PaymentLogo = styled.div`
  height: 28px;
  width: 52px;
  ${({ $image }) =>
    $image &&
    css`
      background-image: url(${staticFile(`img/${$image}`)});
    `}
  background-size: contain;
  background-repeat: no-repeat;
  background-position: center;
`;

const CardWrapper = styled(Flex)`
  gap: 10px;
  ${({ $isNewMobileDesign }) =>
    $isNewMobileDesign &&
    css`
      gap: 16px;
    `}
`;

const Text = styled.h3`
  font-family: ${({ theme }) => theme.font.family.websiteSemiBold};
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  line-height: 1.41;
  color: var(--light-values-white);
  ${({ $isNewMobileDesign }) =>
    $isNewMobileDesign &&
    css`
      color: #fff;
      text-align: center;
      font-size: 18px;
      font-style: normal;
      font-weight: 400;
      line-height: 32px;
    `}
`;

const AccpetedPayment = styled(Flex)`
  gap: 10px;
  padding-bottom: 10px;
  flex-direction: column;
  @media (min-width: 768px) {
    flex-direction: row;
  }
  ${({ $isNewMobileDesign }) =>
    $isNewMobileDesign &&
    css`
      gap: 16px;
      padding-bottom: 32px;
    `}
`;

const CompanyRegistrationNumber = styled.div`
  font-family: ${({ theme }) => theme.font.family.websiteMedium};
  font-size: 10px;
  width: 100%;
  padding: 0 0px;
  text-align: center;
  color: var(--neutral-values-gray-light);
`;

const CompanyName = styled.div`
  font-family: ${({ theme }) => theme.font.family.websiteMedium};
  font-size: 12px;
  color: #8c8cb7;
  width: 100%;
  padding: 0 35px;
  ${({ $isNew }) =>
    $isNew &&
    css`
      padding: 0;
      margin: 12px 0 50px;
    `}
  ${({ $newDesign }) =>
    $newDesign &&
    css`
      display: flex;
      justify-content: center;
      color: var(--neutral-values-gray-light);
      text-align: center;
    `}
`;
const LinkWrapper = styled(Flex)`
  cursor: pointer;
  font-family: ${({ theme }) => theme.font.family.websiteSemiBold};
  font-size: 11px;
  font-weight: 600;
  line-height: 1.45;
  color: var(--light-values-white);
  text-decoration: none;
  ${({ theme }) => theme.max('md')`
      gap: 12px;
  `}
`;

const LinkFlex = styled(Flex)`
  > * + * {
    margin-inline-start: 24px;
    margin-bottom: 8px;
  }
  a {
    text-decoration: none;
  }
  ${({ theme }) => theme.max('md')`
    flex-wrap: wrap;
    > * + * {
      margin-inline-start: 8px;
    }
    justify-content: center;
  `}
  ${({ $isNewMobileDesign }) =>
    $isNewMobileDesign &&
    css`
      > * + * {
        margin-inline-start: 24px !important;
      }
    `}
`;

const FottorWrapper = styled(Flex)`
  background-color: var(--dark-values-black);
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 40px 80px;
  ${({ theme }) => theme.max('md')`
    padding: 20px;
    align-items: center;
    justify-content: center;
  `}
`;
const Wrapper = styled(Flex)`
  background-color: var(--dark-values-black);
  width: 100%;
`;
const StyledFlex = styled(Flex)`
  gap: 40px;
  padding: 40px 0;
  ${({ theme: { isRTL } }) =>
    isRTL &&
    css`
      text-align: right;
    `}
  ${({ theme }) => theme.max('md')`
    flex-direction: column;
    text-align: center;
    width: 100%;
  `}
  ${({ $hideOneYear }) =>
    $hideOneYear &&
    css`
      justify-content: center;
    `}
  ${({ $isNewMobileDesign }) =>
    $isNewMobileDesign &&
    css`
      padding: 24px;
    `}
  ${({ $isMonthFirstWeekLater }) =>
    $isMonthFirstWeekLater &&
    css`
      flex-direction: row-reverse;
    `}
`;
const PlanDescription = styled.p`
  font-family: ${({ theme }) => theme.font.family.websiteMedium};
  font-size: 13px;
  font-weight: 500;
  line-height: 1.54;
  color: var(--neutral-values-gray-light);
  margin: 0;
  ${({ $isNewMobileDesign }) =>
    $isNewMobileDesign &&
    css`
      color: #c4c4c7;
      text-align: center;
      font-style: normal;
      font-weight: 400;
      line-height: 20px;
    `}
`;

const PlanTitle = styled.h3`
  font-family: ${({ theme }) => theme.font.family.websiteSemiBold};
  font-size: 17px;
  font-weight: 600;
  line-height: 1.41;
  color: var(--light-values-white);
  width: 100%;
  margin: 0;
`;
const Plans = styled(Flex)`
  gap: 16px;
`;

const Options = styled(Flex)`
  gap: 40px;
  ${({ $isHalf }) =>
    $isHalf &&
    css`
      width: 50%;
    `}
  ${({ theme }) => theme.max('md')`
    width:100%;
  `}
${({ $isOneShow }) =>
    $isOneShow &&
    css`
      width: 100%;
    `}
`;

const Title = styled.h2`
  font-family: ${({ theme }) => theme.font.family.websiteSemiBold};
  font-size: 22px;
  font-weight: 600;
  line-height: 1.36;
  text-align: center;
  color: var(--light-values-white);
  margin: 0;
  padding-top: 80px;
  ${({ $isNewMobileDesign }) =>
    $isNewMobileDesign &&
    css`
      color: #fff;
      text-align: center;
      font-family: ${({ theme }) => theme.font.family.websiteSemiBold};
      font-size: 24px;
      font-style: normal;
      font-weight: 400;
      line-height: 30px;
      padding-top: 32px;
    `}
`;

const StyledContainer = styled(Container)`
  align-items: center;
  width: 100%;
  ${({ $isNewMobileDesign }) =>
    $isNewMobileDesign &&
    css`
      padding: 0 16px !important;
    `}
`;

const Main = styled(Flex)`
  background-color: #3a3a43;
  margin-top: 80px;
  gap: 1px;
  width: 100%;
  ${({ theme }) => theme.max('md')`
    margin: 0;
  `}
`;

const CompanySupportMail = styled.div`
  font-family: ${({ theme }) => theme.font.family.websiteMedium};
  font-size: 14px;
  width: 100%;
  padding: 0 0px;
  text-align: center;
  color: var(--neutral-values-gray-light);
`;
